<template>
  <div class="animated fadeIn">
    <b-card>
      <p class="h2">利用状況</p>
      <p>利用状況を表示します。</p>

      <!-- ▼ トグルボタン -->
      <div class="toggle-section my-3 py-2">
        <div class="toggle-buttons">
          <b-button
            size="lg"
            variant="outline-primary"
            :class="{ active: currentView === 'summary' }"
            @click="currentView = 'summary'"
          >
            集計履歴
          </b-button>
          <b-button
            size="lg"
            variant="outline-primary"
            :class="{ active: currentView === 'history' }"
            @click="currentView = 'history'"
          >
            詳細履歴
          </b-button>
        </div>
      </div>

      <!-- ▼ 詳細履歴 -->
      <div v-if="currentView === 'history'" class="history">
        <form class="form-inline" style="margin-bottom: 16px;">
          <div class="form-group">
            開始日
            <b-form-input v-model="starttime" type="date" style="margin-left: 8px;"></b-form-input>
          </div>
          <div class="form-group mx-sm-3">
            終了日
            <b-form-input v-model="endtime" type="date" style="margin-left: 8px;"></b-form-input>
          </div>
          <b-button variant="primary" @click="refresh()">
            <i class="fa fa-refresh"></i> 更新
          </b-button>
        </form>

        <div class="history-content">
          <div v-if="isLoading" class="history-loading">
            <spinner
              :line-size="6"
              :size="100"
              :speed="0.8"
              line-fg-color="#B23E53"
            ></spinner>
            <p class="loading-text">データを読み込んでいます...</p>
          </div>

          <div v-else>
            <table class="table-sm table-striped table-borderless">
              <thead>
                <tr>
                  <th width="140">日時</th>
                  <th>履歴</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in paginatedHistoryItems" :key="item.unixtime">
                  <td>{{ item.time }}</td>
                  <td>{{ item.text }}</td>
                </tr>
              </tbody>
            </table>

            <b-pagination
              v-if="historyPageCount > 1"
              v-model="historyCurrentPage"
                :total-rows="historyTotalRows || items.length"
              :per-page="historyPageSize"
              align="center"
              class="custom-pagination history-pagination"
              first-text="«"
              prev-text="前へ"
              next-text="次へ"
              last-text="»"
            ></b-pagination>
          </div>
        </div>
      </div>

      <!-- ▼ 集計履歴モード -->
      <div v-else-if="currentView === 'summary'">
        <p class="h3" style="font-size: 1.2rem;">集計</p>
        <div class="summary-box">
          <div class="summary-item">
            <p class="summary-title">登録物件数</p>
            <p class="summary-value">{{ countupload }}</p>
          </div>
          <div class="divider"></div>
          <div class="summary-item">
            <p class="summary-title">公開物件数</p>
            <p class="summary-value">{{ countpublic }}</p>
          </div>
          <div class="divider"></div>
          <div class="summary-item">
            <p class="summary-title">総閲覧数</p>
            <p class="summary-value">{{ countdownload }}</p>
          </div>
        </div>

        <!-- ▼ プラン一覧表 -->
        <p class="h3" style="font-size: 1.2rem;">施主別閲覧状況</p>

        <!-- ▼ ローディング中 -->
        <div v-if="isPlanLoading" class="loading-container">
          <spinner
            :line-size="4"
            :size="70"
            :speed="0.8"
            line-fg-color="#B23E53"
            style="margin:20px;"
          ></spinner>
          <p>データを読み込んでいます...</p>
        </div>

        <!-- ▼ データ表示 -->
        <div v-else>
          <table class="table-sm table-striped table-borderless" style="margin-top: 10px;">
            <thead>
              <tr>
                <th>アップロード時間</th>
                <th>プラン名</th>
                <th>総閲覧数</th>
                <th>閲覧履歴</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in planTableRows" :key="row.key">
                <template v-if="!row.isDetail">
                  <td>{{ dateToString(new Date(row.plan.updatetime)) }}</td>
                  <td>{{ row.plan.name }}</td>
                  <td>
                    <span v-if="isPlanLoading || row.plan.isLoadingLogs">
                      <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                    </span>
                    <span v-else-if="row.plan.totalViews !== null && row.plan.totalViews !== undefined">{{ row.plan.totalViews + "回" }}</span>
                    <span v-else>—</span>
                  </td>
                  <td>
                    <b-button class="pager-btn" size="sm" @click="toggleDetail(row.plan)">
                      {{ row.plan.showDetail ? '閉じる' : '詳細' }}
                    </b-button>
                  </td>
                </template>
                <template v-else>
                  <td colspan="4" class="detail-cell">
                    <div class="accordion open">
                      <h4>閲覧履歴（{{ (row.plan.totalViews !== null && row.plan.totalViews !== undefined) ? row.plan.totalViews : (row.plan.viewDates ? row.plan.viewDates.length : 0) }}回）</h4>
                      <div class="dates-wrap">
                        <ul class="dates">
                          <li v-for="(d, idx) in visibleDates(row.plan)" :key="idx">
                            ・{{ d }}
                          </li>
                        </ul>
                      </div>
                      <div class="pager" v-if="pageCount(row.plan) > 1">
                        <button class="pager-btn" @click="prevPage(row.plan)" :disabled="row.plan.page === 0">前へ</button>
                        <span class="pageinfo">{{ row.plan.page + 1 }} / {{ pageCount(row.plan) }}</span>
                        <button class="pager-btn" @click="nextPage(row.plan)" :disabled="row.plan.page >= pageCount(row.plan) - 1">次へ</button>
                      </div>
                    </div>
                  </td>
                </template>
              </tr>
            </tbody>
          </table>

          <!-- ▼ ページャ（プラン一覧） -->
          <b-pagination
            v-if="planPageCount > 1"
            :value="currentPlanPage"
            :total-rows="plans.length"
            :per-page="pageSize"
            align="center"
            class="custom-pagination plan-pagination"
            first-text="«"
            prev-text="前へ"
            next-text="次へ"
            last-text="»"
            @input="onPlanPageInput"
          ></b-pagination>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import Store from '@/store'
import ApiAccess from '@/components/ApiAccess'
import Spinner from 'vue-simple-spinner'

export default {
  name: 'tables',
  mixins: [ApiAccess],
  components: { Spinner },
  data: () => ({
    items: [],
    allLogs: [],
    isLoading: false,
    isPlanLoading: false,
    starttime: '2018-07-01',
    endtime: '2018-07-31',
    currentView: 'summary',
    countupload: 0,
    countpublic: 0,
    countdownload: 0,
    plans: [],
    currentPlanPage: 1,
    pageSize: 50,
    historyCurrentPage: 1,
    historyPageSize: 50,
    historyTotalRows: null
  }),
  created () {
    const startDate = new Date()
    const endDate = new Date()
    // default to a 1-week range: start = today - 7 days, end = today
    startDate.setDate(startDate.getDate() - 7)
    const formatDateOnly = (date) => {
      const y = date.getFullYear()
      const m = ('00' + (date.getMonth() + 1)).slice(-2)
      const d = ('00' + date.getDate()).slice(-2)
      return `${y}-${m}-${d}`
    }
    this.starttime = formatDateOnly(startDate)
    this.endtime = formatDateOnly(endDate)
    this.getDatas()

    if (this.currentView === 'summary') {
      this.loadSummaryData()
    }
  },
  watch: {
    currentView (val) {
      if (val === 'summary') {
        this.loadSummaryData()
      }
      if (val === 'history') {
        // reset to first page and fetch
        this.fetchHistoryPage(0)
      }
    },
    async currentPlanPage (newPage) {
      // When user changes plan page in summary view, fetch logs for newly visible plans
      if (this.currentView !== 'summary') return
      // show loading indicator for the plan list while fetching
      this.isPlanLoading = true
      try {
        const visible = this.paginatedPlans || this.plans.slice((newPage - 1) * this.pageSize, (newPage - 1) * this.pageSize + this.pageSize)
        const tasks = visible.map(plan => {
          // skip if already loaded
          if (plan.viewDates && plan.viewDates.length > 0) return Promise.resolve()
          return this.fetchPlanLogs(plan, 0, 10)
        })
        await Promise.allSettled(tasks)
      } catch (e) {
        console.error('currentPlanPage watcher failed:', e)
      } finally {
        this.isPlanLoading = false
      }
    },
    historyCurrentPage (newPage) {
      // fetch logs for the selected page
      if (this.currentView === 'history') {
        this.fetchHistoryPage(newPage - 1)
      }
    }
  },
  computed: {
    paginatedPlans () {
      const start = (this.currentPlanPage - 1) * this.pageSize
      return this.plans.slice(start, start + this.pageSize)
    },
    planPageCount () {
      return Math.ceil(this.plans.length / this.pageSize)
    },
    paginatedHistoryItems () {
      // when server provides paged results, `items` contains only current page
      // otherwise `items` may contain all logs and we paginate client-side
      if (this.historyTotalRows) {
        // server returned current page in items
        return this.items || []
      }
      const start = (this.historyCurrentPage - 1) * this.historyPageSize
      return this.items.slice(start, start + this.historyPageSize)
    },
    historyPageCount () {
      // If server returned totalRows value, prefer server-driven count
      if (this.historyTotalRows) return Math.ceil(this.historyTotalRows / this.historyPageSize)
      return Math.ceil(this.items.length / this.historyPageSize)
    },
    planTableRows () {
      return this.paginatedPlans.reduce((rows, plan) => {
        rows.push({ key: `main-${plan.planid}`, plan, isDetail: false })
        if (plan.showDetail) {
          rows.push({ key: `detail-${plan.planid}`, plan, isDetail: true })
        }
        return rows
      }, [])
    }
  },
  methods: {
    async loadSummaryData () {
      this.isPlanLoading = true
      await Promise.all([
        this.getUserInfo(),
        this.getPlanList()
      ])
      // Server should provide totalViews when possible. If not, fetch recent logs/counts for plans
      try {
        // fetch logs for visible plans (current page) so the summary view can show counts and recent dates
        const visible = this.paginatedPlans || this.plans.slice(0, this.pageSize)
        // limit recent items per plan to 10 for summary
        const tasks = visible.map(plan => this.fetchPlanLogs(plan, 0, 10))
        await Promise.allSettled(tasks)
      } catch (e) {
        console.error('loadSummaryData: fetchPlanLogs failed', e)
      }
      this.isPlanLoading = false
    },

    async getDatas () {
      // default: fetch first page of history (page index 0)
      await this.fetchHistoryPage(0)
    },

    async fetchHistoryPage (pageIndex) {
      this.isLoading = true
      try {
        // API expects page starting at 0 or 1? we send pageIndex (0-based) and per_page
        const res = await this.$_Api_GetLogList(
          Store.state.token,
          Store.state.companyid,
          this.starttime,
          this.endtime,
          pageIndex,
          this.historyPageSize
        )

        // If API returns paged response with data and total, honor it.
        if (res) {
          // Common patterns: res may be an axios response where res.data is
          // either an array, an object with items, or an API Gateway proxy
          // shape where res.data.body is a JSON string. Normalize accordingly.
          let payload = null

          if (res.data) {
            // Case: API Gateway proxy -> body is a JSON string
            if (res.data.body && typeof res.data.body === 'string') {
              try {
                payload = JSON.parse(res.data.body)
              } catch (e) {
                console.error('Failed to parse res.data.body as JSON', e)
                payload = null
              }
            } else {
              // res.data is already the payload
              payload = res.data
            }
          }

          // If payload is an array, treat as items page
          if (Array.isArray(payload)) {
            this.items = payload
            this.historyTotalRows = (res.headers && (res.headers['x-total-count'] || res.headers['X-Total-Count'])) ? parseInt(res.headers['x-total-count'] || res.headers['X-Total-Count']) : null
          } else if (payload && payload.items) {
            this.items = payload.items
            this.historyTotalRows = payload.total || (res.headers && (res.headers['x-total-count'] || res.headers['X-Total-Count']) ? parseInt(res.headers['x-total-count'] || res.headers['X-Total-Count']) : null)
          } else if (res && Array.isArray(res)) {
            // defensive: whole response is an array
            this.items = res
            this.historyTotalRows = null
          } else {
            // fallback: set items to empty array
            this.items = []
            this.historyTotalRows = 0
          }
        }
      } catch (e) {
        console.error(e)
        this.items = []
        this.historyTotalRows = 0
      } finally {
        this.historyCurrentPage = pageIndex + 1
        this.isLoading = false
      }
    },

    // Fetch logs for a specific plan (paged). Uses optional planid parameter in ApiAccess.
    // fetchPlanLogs(plan, page, perPage, starttime, endtime)
    // If starttime/endtime are null/undefined, the API will search the full range.
    async fetchPlanLogs (plan, page = 0, perPage = 50, starttime = undefined, endtime = undefined) {
      // mark loading flag on plan to allow UI feedback
      this.$set(plan, 'isLoadingLogs', true)
      try {
        const res = await this.$_Api_GetLogList(
          Store.state.token,
          Store.state.companyid,
          starttime,
          endtime,
          page,
          perPage,
          plan.planid
        )

        let payload = null
        if (res && res.data) {
          if (res.data.body && typeof res.data.body === 'string') {
            try {
              payload = JSON.parse(res.data.body)
            } catch (e) {
              console.error('Failed to parse res.data.body as JSON', e)
              payload = null
            }
          } else {
            payload = res.data
          }
        }

        // Expect payload to be either array of logs or { items:[], total: n }
        let logs = []
        if (Array.isArray(payload)) logs = payload
        else if (payload && payload.items) logs = payload.items

        // convert to viewDates (strings) and attach to plan
        this.$set(plan, 'viewDates', logs.map(l => this.dateToString(new Date(l.time))))
        this.$set(plan, 'totalViews', (payload && payload.total) ? payload.total : logs.length)
        this.$set(plan, 'page', page)
      } catch (e) {
        console.error('fetchPlanLogs failed:', e)
      } finally {
        this.$set(plan, 'isLoadingLogs', false)
      }
    },

    async getUserInfo () {
      const res = await this.$_Api_GetUserInfo(Store.state.token, Store.state.companyid)
      if (res && res.data) {
        this.countupload = res.data.countuplaod
        this.countpublic = res.data.countpublic
        this.countdownload = res.data.countdownload
      }
    },

    async getPlanList () {
      this.isLoading = true
      try {
        const res = await this.$_Api_GetPlanList(Store.state.token, Store.state.companyid)
        if (res) {
          this.plans = res.map(p => ({
            ...p,
            // do not default to 0 to avoid transient zero display; null means 'unknown' until fetched
            totalViews: (p.totalViews !== undefined && p.totalViews !== null) ? p.totalViews : null,
            viewDates: p.recentViews || [],
            showDetail: false,
            page: 0,
            // number of dates per detail page
            detailPageSize: 10
          }))
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.isLoading = false
      }
    },

    updatePlanViewCounts () {
      if (!this.allLogs || !this.plans) return
      this.plans.forEach(plan => {
        const planLogs = this.allLogs.filter(log =>
          log.type === 8 && log.planid === plan.planid
        )
        plan.totalViews = planLogs.length
        plan.viewDates = planLogs.map(l => this.dateToString(new Date(l.time)))
      })
    },

    async toggleDetail (plan) {
      plan.showDetail = !plan.showDetail
      // When opening detail, always reset to first page and refresh its contents
      if (plan.showDetail) {
        // reset page index (0-based)
        this.$set(plan, 'page', 0)
        // clear any previously loaded page data to avoid showing stale page slices
        // (this ensures visibleDates will reflect page 0 after fetch)
        this.$set(plan, 'viewDates', [])
        // fetch first page of detail items (use plan.detailPageSize if provided)
        const perPage = plan.detailPageSize || 10
        await this.fetchPlanLogs(plan, 0, perPage)
      }
    },

    visibleDates (plan) {
      // If server returns page items only, viewDates already contains the current page items.
      // For client-side full-array mode, viewDates may be the whole list; in that case slice by page.
      const pageSize = plan.detailPageSize || 10
      if (plan.viewDates && plan.viewDates.length <= pageSize) {
        return plan.viewDates || []
      }
      const start = plan.page * pageSize
      return (plan.viewDates || []).slice(start, start + pageSize)
    },

    pageCount (plan) {
      const pageSize = plan.detailPageSize || 10
      if (plan.totalViews !== null && plan.totalViews !== undefined) {
        return Math.max(1, Math.ceil(plan.totalViews / pageSize))
      }
      // fallback to client-side length
      return Math.max(1, Math.ceil((plan.viewDates && plan.viewDates.length) ? plan.viewDates.length / pageSize : 1))
    },

    async nextPage (plan) {
      if (plan.page < this.pageCount(plan) - 1) {
        const newPage = plan.page + 1
        // fetch next detail page from server to keep amounts small
        await this.fetchPlanLogs(plan, newPage, plan.detailPageSize)
      }
    },

    async prevPage (plan) {
      if (plan.page > 0) {
        const newPage = plan.page - 1
        await this.fetchPlanLogs(plan, newPage, plan.detailPageSize)
      }
    },

    nextPlanPage () {
      if (this.currentPlanPage < this.planPageCount - 1) this.onPlanPageInput(this.currentPlanPage + 1)
    },

    prevPlanPage () {
      if (this.currentPlanPage > 0) this.onPlanPageInput(this.currentPlanPage - 1)
    },

    onPlanPageInput (newPage) {
      // immediately show loading state and then set the page to trigger watcher
      this.isPlanLoading = true
      // set currentPlanPage; watcher will fetch visible plans and clear isPlanLoading
      this.currentPlanPage = newPage
    },

    async refresh () {
      this.items.length = 0
      await this.getDatas()
    },

    dateToString (date) {
      const y = date.getFullYear()
      const m = ('00' + (date.getMonth() + 1)).slice(-2)
      const d = ('00' + date.getDate()).slice(-2)
      const h = ('00' + date.getHours()).slice(-2)
      const min = ('00' + date.getMinutes()).slice(-2)
      return `${y}-${m}-${d} ${h}:${min}`
    }
  }
}
</script>

<style>
table {
  width: 100%;
}
th {
  background: #3e515b;
  color: white;
}

/* ▼ トグルボタン */
.toggle-section {
  border-top: 2px solid #ccc;
  border-bottom: 2px solid #ccc;
  min-height: 75px;
  display: flex;
  align-items: center;
  padding: 10px 0;
}
.toggle-buttons {
  display: flex;
  justify-content: center;
  gap: 20px; /* 少し広めに */
}
.toggle-buttons .btn {
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  padding: 8px 24px;
  font-size: 16px;
  min-width: 160px;
}
.toggle-buttons .btn.active {
  color: white !important;
}

/* ▼ 施主別閲覧状況テーブル */
.table-sm {
  width: 100%;
  margin: 0;
  border-collapse: collapse;
  font-size: 14px;
}

.table-sm th,
.table-sm td {
  text-align: center;
  vertical-align: middle;
  padding: 8px 6px;
}

/* 列幅調整（必要に応じて調整）*/
.table-sm th:nth-child(1),
.table-sm td:nth-child(1) {
  width: 20%; /* アップロード時間 */
}
.table-sm th:nth-child(2),
.table-sm td:nth-child(2) {
  width: 45%; /* プラン名 */
}
.table-sm th:nth-child(3),
.table-sm td:nth-child(3) {
  width: 20%; /* 総閲覧数 */
}
.table-sm th:nth-child(4),
.table-sm td:nth-child(4) {
  width: 15%; /* 操作 */
}

.table-sm th {
  background: #3e515b;
  color: white;
  font-weight: 600;
}

/* ▼ 集計ボックス */
.summary-box {
  display: flex;
  justify-content: space-around;
  align-items: stretch;
  background-color: #f5f5f5;
  border: 2px solid #ccc;
  border-radius: 10px;
  padding: 15px 10px 15px 10px;
  margin-top: 10px;
  margin-bottom: 20px;
  box-shadow: 2px 2px 5px rgba(0,0,0,0.1);
  text-align: center;
}

/* ▼ 各項目 */
.summary-item {
  flex: 1;
}
.summary-title {
  font-size: 14px;
  color: #3e515b;
  font-weight: 600;
  margin: 0px 10px 15px 10px;
}
.summary-value {
  font-size: 40px;
  margin: 0px 10px 15px 10px;
  color: #3e515b;
}
.divider {
  width: 2px;
  background-color: #ccc;
  margin: 10px 10px 10px 10px;
}

/* ▼ アコーディオン詳細 */
.detail-cell {
  padding: 0 !important;
  background: #f7f9fc;
}

.accordion.open {
  padding: 16px;
  border-top: 1px solid #e6edf5;
  text-align: left;
}

.accordion h4 {
  margin: 4px 0 8px;
  color: #0f172a;
  font-size: 14px;
  text-align: left;
}

.dates-wrap {
  margin-bottom: 10px;
}

.dates-scroll {
  max-height: none;
  overflow-y: visible;
}

.dates {
  margin: 0;
  padding-left: 20px;
  text-align: left;
}

.dates li {
  list-style: none;
  padding: 6px 4px;
  border-bottom: 1px dashed #e5e7eb;
  text-align: left;
}

/* ▼ ページャ */
.pager-btn {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: .5rem;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid #B23E53;
  color: #B23E53;
  background: #fff;
  font-weight: 600;
  transition: background 0.2s, color 0.2s;
}
.pager-btn:hover {
  background: #B23E53;
  color: #fff;
}
.pager-btn:disabled {
  opacity: .45;
  cursor: not-allowed;
}
.pager {
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;
}
.pageinfo {
  color: #64748b;
  font-size: 12px;
}

/* ▼ ローディング */
.loading-container {
  text-align: center;
  padding: 40px;
  color: #555;
  font-size: 16px;
}

.history table {
  width: 100%;
  border-collapse: collapse;
}

.history th,
.history td {
  text-align: left !important;
  padding: 6px 10px;
  vertical-align: middle;
}

/* 履歴一覧の中央ローディング */
.history-content {
  min-height: 160px; /* テーブルが通常占める領域を確保 */
}
.history-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}
.history-loading .loading-text {
  margin-top: 16px;
  font-size: 18px;
  color: #555;
}

/* 履歴ページのテーブルとページャの間隔調整 */
.history-pagination {
  margin-top: 12px;
  margin-bottom: 8px;
}

/* 集計（プラン一覧）用ページャを下に12px移動 */
.plan-pagination {
  margin-top: 12px;
}

.history th {
  background: #3e515b;
  color: white;
  font-weight: 600;
}

</style>



// WEBPACK FOOTER //
// src/views/home/State.vue